import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'

import Form from '../../../components/Form'
import EditSection from '../../../features/components/Forms/EditForm/EditSection'
import RepresentativeFormFields from '../RepresentativeSection/RepresentativeFormFields'
import ActionText from '../../../components/ActionText'

import { usePurifiedFormik } from '../../../hooks/formHooks/usePurifiedFormik'

import {
  clearCreateSelfAccountRepresentative,
  createSelfAccountRepresentative
} from '../../../modules/actions/selfAccounts'
import {
  createSelfAccountRepresentativeErrorSelector,
  createSelfAccountRepresentativeIsLoadingSelector,
  createSelfAccountRepresentativeWasCreatedSelector,
  getSelfAccountRepresentativesSelector
} from '../../../modules/selectors/selfAccounts'

import { initialRepresentativeValue, representativeValidationSchema } from '../fields'

const CreateRepresentativeForm = ({ accountId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isFormVisible, setIsFormVisible] = useState(false)

  const successCreate = useSelector(createSelfAccountRepresentativeWasCreatedSelector)
  const selfAccountRepresentatives = useSelector(getSelfAccountRepresentativesSelector)

  const handleSubmit = useCallback(
    values => {
      dispatch(
        createSelfAccountRepresentative({
          ...values,
          account: accountId
        })
      )
    },
    [dispatch, accountId]
  )

  const formik = useFormik({
    initialValues: initialRepresentativeValue,
    validationSchema: representativeValidationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearCreate = useCallback(() => {
    dispatch(clearCreateSelfAccountRepresentative())
    setIsFormVisible(false)
  }, [dispatch])

  const handleShowForm = useCallback(() => {
    setIsFormVisible(true)
  }, [])

  if (!isFormVisible) {
    return (
      <ActionText isDark={true} onClick={handleShowForm}>
        {t('+ Add new representative')}
      </ActionText>
    )
  }

  return (
    <EditSection
      title={t('representativeNumber_ordinal', { count: selfAccountRepresentatives.length + 1 })}
      isSectionOpen={true}
      toggleSectionVisibility={() => setIsFormVisible(false)}
    >
      <Form
        formik={purifiedFormik}
        formName="createRepresentative"
        successSubmit={successCreate}
        clearHandler={handleClearCreate}
        errorSelector={createSelfAccountRepresentativeErrorSelector}
        isLoadingSelector={createSelfAccountRepresentativeIsLoadingSelector}
        submitText="Save"
      >
        <RepresentativeFormFields formik={formik} />
      </Form>
    </EditSection>
  )
}
CreateRepresentativeForm.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

export default CreateRepresentativeForm
